import { useState } from 'react';
import { api } from '@/lib/api-client';
import { Coins, Zap, Bell, CheckCircle, ArrowRight, CreditCard, Shield, Clock, ArrowLeft } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

const packages = [
  { amount: 50, bonus: 0, label: 'Basic' },
  { amount: 100, bonus: 5, label: 'Standard', popular: true },
  { amount: 250, bonus: 25, label: 'Pro' },
  { amount: 500, bonus: 75, label: 'Business' },
];

const txHistory = [
  { id: 'tx_1', date: '2026-02-15', type: 'Purchase', amount: 100, balance: 287.50, desc: '$100 credit + $5 bonus' },
  { id: 'tx_2', date: '2026-02-10', type: 'Usage', amount: -42.30, balance: 187.50, desc: 'Instance: prod-assistant' },
  { id: 'tx_3', date: '2026-02-05', type: 'Usage', amount: -28.10, balance: 229.80, desc: 'Instance: support-agent' },
  { id: 'tx_4', date: '2026-02-01', type: 'Purchase', amount: 250, balance: 257.90, desc: '$250 credit + $25 bonus' },
  { id: 'tx_5', date: '2026-01-28', type: 'Usage', amount: -15.60, balance: 7.90, desc: 'Instance: dev-bot' },
];

export default function Credits() {
  const [balance, setBalance] = useState(187.50);
  const [autoRecharge, setAutoRecharge] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [selectedPkg, setSelectedPkg] = useState(null);
  const [checkoutStep, setCheckoutStep] = useState('review');
  const [processing, setProcessing] = useState(false);

  const openCheckout = (pkg) => {
    setSelectedPkg(pkg);
    setCheckoutStep('review');
    setCheckoutOpen(true);
  };

  const confirmPurchase = async () => {
    setProcessing(true);
    setCheckoutStep('processing');
    await new Promise(r => setTimeout(r, 2000));
    const total = selectedPkg.amount + selectedPkg.bonus;
    setBalance(prev => prev + total);
    setProcessing(false);
    setCheckoutStep('success');
  };

  const closeCheckout = () => {
    setCheckoutOpen(false);
    setSelectedPkg(null);
    setCheckoutStep('review');
  };

  return (
    <div data-testid="credits-page" className="space-y-6">
      <h1 className="text-2xl font-bold text-white font-mono">Credits</h1>

      <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-sm p-8 text-center">
        <span className="text-xs font-mono text-zinc-500 uppercase tracking-widest">Current Balance</span>
        <p data-testid="credit-balance" className="text-5xl font-mono font-bold text-emerald-400 mt-2">${balance.toFixed(2)}</p>
        <p className="text-xs font-mono text-zinc-600 mt-2">Credits are used for LLM API costs and compute overages</p>
      </div>

      <h2 className="text-lg font-mono text-white font-bold">Purchase Credits</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {packages.map(pkg => (
          <div
            key={pkg.amount}
            data-testid={`credit-pkg-${pkg.amount}`}
            className={`bg-[#0A0A0A] border rounded-sm p-6 text-center transition-all relative ${pkg.popular ? 'border-emerald-500/30 shadow-[0_0_20px_rgba(16,185,129,0.08)]' : 'border-zinc-800 hover:border-zinc-700'}`}
          >
            {pkg.popular && (
              <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-emerald-500 text-black text-[10px] font-mono font-bold rounded-sm">
                BEST VALUE
              </div>
            )}
            <Coins size={24} className="text-emerald-400 mx-auto mb-3" />
            <p className="text-xs font-mono text-zinc-500 mb-1">{pkg.label}</p>
            <p className="text-2xl font-mono font-bold text-white">${pkg.amount}</p>
            {pkg.bonus > 0 && (
              <p className="text-xs font-mono text-emerald-400 mt-1">+${pkg.bonus} bonus ({Math.round(pkg.bonus / pkg.amount * 100)}% extra)</p>
            )}
            <div className="text-xs font-mono text-zinc-600 mt-2 mb-4">
              Total: <span className="text-white">${pkg.amount + pkg.bonus}</span> credits
            </div>
            <button
              data-testid={`purchase-btn-${pkg.amount}`}
              onClick={() => openCheckout(pkg)}
              className={`w-full font-mono text-sm rounded-sm py-2.5 transition-all click-scale flex items-center justify-center gap-2 ${pkg.popular ? 'bg-emerald-500 text-black font-bold hover:bg-emerald-400 shadow-[0_0_15px_rgba(16,185,129,0.3)]' : 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20'}`}
            >
              Purchase <ArrowRight size={14} />
            </button>
          </div>
        ))}
      </div>

      {/* Controls */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-[#0A0A0A] border border-zinc-800 rounded-sm p-5 flex items-center gap-4">
          <Zap size={20} className="text-cyan-400 flex-shrink-0" />
          <div className="flex-1">
            <h3 className="text-sm font-mono text-white font-bold">Auto-Recharge</h3>
            <p className="text-xs text-zinc-600">Auto-add $100 when balance drops below $10</p>
          </div>
          <button data-testid="auto-recharge-toggle" onClick={() => setAutoRecharge(!autoRecharge)} className={`w-10 h-5 rounded-full transition-all flex-shrink-0 ${autoRecharge ? 'bg-emerald-500' : 'bg-zinc-700'}`}>
            <div className={`w-4 h-4 rounded-full bg-white transition-transform ${autoRecharge ? 'translate-x-5' : 'translate-x-0.5'}`} />
          </button>
        </div>
        <div className="bg-[#0A0A0A] border border-zinc-800 rounded-sm p-5 flex items-center gap-4">
          <Bell size={20} className="text-yellow-500 flex-shrink-0" />
          <div className="flex-1">
            <h3 className="text-sm font-mono text-white font-bold">Low Balance Alert</h3>
            <p className="text-xs text-zinc-600">Email notification when balance below $25</p>
          </div>
          <span className="text-xs font-mono text-emerald-400">Active</span>
        </div>
      </div>

      {/* Transaction History */}
      <div>
        <h2 className="text-lg font-mono text-white font-bold mb-4">Transaction History</h2>
        <div className="bg-[#0A0A0A] border border-zinc-800 rounded-sm overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-800">
                {['Date', 'Type', 'Description', 'Amount', 'Balance'].map(h => (
                  <th key={h} className="text-left py-3 px-4 text-xs font-mono text-zinc-500 uppercase tracking-widest">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {txHistory.map(tx => (
                <tr key={tx.id} data-testid={`tx-${tx.id}`} className="border-b border-zinc-800/50 hover:bg-white/[0.02]">
                  <td className="py-3 px-4 text-sm font-mono text-zinc-400">{tx.date}</td>
                  <td className="py-3 px-4">
                    <span className={`text-xs font-mono px-2 py-0.5 rounded-sm ${tx.type === 'Purchase' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-cyan-500/10 text-cyan-400'}`}>{tx.type}</span>
                  </td>
                  <td className="py-3 px-4 text-sm font-mono text-zinc-400">{tx.desc}</td>
                  <td className={`py-3 px-4 text-sm font-mono font-bold ${tx.amount > 0 ? 'text-emerald-400' : 'text-zinc-400'}`}>
                    {tx.amount > 0 ? '+' : ''}{tx.amount < 0 ? '-' : ''}${Math.abs(tx.amount).toFixed(2)}
                  </td>
                  <td className="py-3 px-4 text-sm font-mono text-white">${tx.balance.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Checkout Dialog */}
      <Dialog open={checkoutOpen} onOpenChange={closeCheckout}>
        <DialogContent className="bg-[#0A0A0A] border-zinc-800 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white font-mono">
              {checkoutStep === 'success' ? 'Purchase Complete' : checkoutStep === 'processing' ? 'Processing Payment' : 'Confirm Purchase'}
            </DialogTitle>
            <DialogDescription className="text-zinc-500 font-mono text-xs">
              {checkoutStep === 'review' && 'Review your credit purchase details'}
            </DialogDescription>
          </DialogHeader>

          {checkoutStep === 'review' && selectedPkg && (
            <div className="space-y-4 mt-2">
              {/* Order summary */}
              <div className="bg-[#111] border border-zinc-800 rounded-sm p-5 space-y-3">
                <div className="flex justify-between text-sm font-mono">
                  <span className="text-zinc-500">Credit Package</span>
                  <span className="text-white">{selectedPkg.label} — ${selectedPkg.amount}</span>
                </div>
                {selectedPkg.bonus > 0 && (
                  <div className="flex justify-between text-sm font-mono">
                    <span className="text-zinc-500">Bonus Credits</span>
                    <span className="text-emerald-400">+${selectedPkg.bonus}</span>
                  </div>
                )}
                <div className="border-t border-zinc-800 pt-3 flex justify-between text-sm font-mono">
                  <span className="text-zinc-400">Total Credits Added</span>
                  <span className="text-white font-bold">${selectedPkg.amount + selectedPkg.bonus}</span>
                </div>
                <div className="flex justify-between text-sm font-mono">
                  <span className="text-zinc-400">Amount Charged</span>
                  <span className="text-emerald-400 font-bold">${selectedPkg.amount}.00</span>
                </div>
              </div>

              {/* Payment method preview */}
              <div className="flex items-center gap-3 bg-[#111] border border-zinc-800 rounded-sm p-3">
                <CreditCard size={16} className="text-zinc-500" />
                <span className="text-sm font-mono text-zinc-400">Visa ending in 4242</span>
                <span className="ml-auto text-xs font-mono text-emerald-400">Change</span>
              </div>

              <div className="flex items-center gap-2 text-xs text-zinc-600 font-mono">
                <Shield size={12} className="text-emerald-500" /> Secured with 256-bit encryption
              </div>

              <div className="flex gap-3 pt-2">
                <button onClick={closeCheckout} className="flex-1 border border-zinc-800 text-zinc-400 font-mono text-sm rounded-sm py-2.5 hover:border-zinc-700 transition-all">
                  Cancel
                </button>
                <button
                  data-testid="confirm-purchase-btn"
                  onClick={confirmPurchase}
                  className="flex-1 bg-emerald-500 text-black font-mono font-bold text-sm rounded-sm py-2.5 hover:bg-emerald-400 transition-all click-scale shadow-[0_0_15px_rgba(16,185,129,0.3)]"
                >
                  Pay ${selectedPkg.amount}.00
                </button>
              </div>
            </div>
          )}

          {checkoutStep === 'processing' && (
            <div data-testid="checkout-processing" className="text-center py-10">
              <div className="w-12 h-12 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-white font-mono">Processing payment...</p>
              <p className="text-xs text-zinc-500 font-mono mt-1">Please don't close this window</p>
            </div>
          )}

          {checkoutStep === 'success' && selectedPkg && (
            <div data-testid="checkout-success" className="text-center py-8 space-y-3">
              <CheckCircle size={48} className="text-emerald-400 mx-auto" />
              <p className="text-white font-mono text-lg font-bold">Payment Successful</p>
              <div className="bg-[#111] border border-zinc-800 rounded-sm p-4 space-y-2">
                <div className="flex justify-between text-sm font-mono">
                  <span className="text-zinc-500">Credits Added</span>
                  <span className="text-emerald-400 font-bold">+${selectedPkg.amount + selectedPkg.bonus}</span>
                </div>
                <div className="flex justify-between text-sm font-mono">
                  <span className="text-zinc-500">New Balance</span>
                  <span className="text-white font-bold">${balance.toFixed(2)}</span>
                </div>
              </div>
              <button
                data-testid="checkout-done-btn"
                onClick={closeCheckout}
                className="w-full bg-emerald-500 text-black font-mono font-bold text-sm rounded-sm py-2.5 hover:bg-emerald-400 transition-all click-scale mt-2"
              >
                Done
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
